// @flow
import type {
  EventCreateAddress,
  EventCreateCoordinates,
  EventCreateRequest,
} from "redux/api-types/EventCreate";

import { DEFAULT_CLOSE_TIME, DEFAULT_EVENT_STATUS } from "../constants";
import { combineDateAndTime, coerceToDate } from "./dates";
import { getCapacityError } from "./capacity";
import { getClassDuration, getDurationError } from "./duration";
import { getPriceError } from "./costOptionsForm";

export type ScheduledClassDate = {
  classDate?: Date | string | null,
  startTime?: Date | string,
  endTime?: Date | string,
  capacity?: number | string,
  capacityOverridden?: boolean,
};

export type CabelasClassFormValues = {
  locationId?: string,
  locationName?: string,
  address1?: string,
  address2?: string,
  city?: string,
  state?: string,
  postalCode?: string,
  lat?: number | string,
  lng?: number | string,
  eventContentId?: string,
  name?: string,
  tagline?: string,
  description?: string,
  type?: string,
  topics?: Array<string>,
  primaryImageUrl?: string,
  secondaryImageUrl?: string,
  coverImageUrl?: string,
  isWomenOnly?: boolean,
  isMultiDay?: boolean,
  hasLiveFire?: boolean,
  costOption?: string,
  templateId?: string,
  capacity?: number | string,
  maxCapacity?: ?number,
  minDuration?: ?number,
  maxDuration?: ?number,
  price?: number | string,
  pricingError?: string,
  classDate?: Date,
  startTime?: Date,
  endTime?: Date,
  classDates?: Array<ScheduledClassDate>,
  timezone?: string,
  hostIds?: Array<string>,
};

const parseNumber = (value: number | string | void): number =>
  parseFloat(String(value));

const parsePrice = (value: number | string | void): number =>
  Math.round(parseNumber(value) * 100);

type EventSchedule = {
  startDate: string,
  endDate: string,
  closeDate: string,
};

type ParseScheduleCabelasClassesFormsOptions = {
  batchCreationId?: string,
};

export type EventVenue = {
  address: EventCreateAddress,
  coordinates: EventCreateCoordinates,
};

export const createBatchCreationId = (): string =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
    /[xy]/g,
    (character: string): string => {
      const randomValue = Math.floor(Math.random() * 16);
      const value =
        character === "x" ? randomValue : Math.floor(randomValue / 4) + 8;

      return value.toString(16);
    }
  );

const buildCabelasClassEvent = (
  values: CabelasClassFormValues,
  userId: ?string,
  schedule: EventSchedule,
  businessId: ?string,
  batchCreationId?: string,
  eventType: string = "class",
  venue?: EventVenue
): EventCreateRequest => {
  const {
    locationId,
    name,
    tagline,
    description,
    topics,
    primaryImageUrl,
    secondaryImageUrl,
    coverImageUrl,
    isWomenOnly,
    isMultiDay,
    hasLiveFire,
    costOption,
    templateId,
    capacity,
    price,
    timezone,
    hostIds,
  } = values;

  if (
    !locationId ||
    !name ||
    !costOption ||
    !templateId ||
    !timezone ||
    !userId ||
    !businessId
  ) {
    throw new Error("Missing required fields for event creation");
  }

  const imageUrls =
    primaryImageUrl || secondaryImageUrl || coverImageUrl
      ? {
          ...(primaryImageUrl ? { primary: primaryImageUrl } : {}),
          ...(secondaryImageUrl ? { secondary: secondaryImageUrl } : {}),
          ...(coverImageUrl ? { cover: coverImageUrl } : {}),
        }
      : undefined;

  const event: EventCreateRequest = {
    businessId,
    templateId,
    type: eventType,
    name: name.trim(),
    status: DEFAULT_EVENT_STATUS,
    startDate: schedule.startDate,
    endDate: schedule.endDate,
    closeDate: schedule.closeDate,
    timezone,
    capacity: Number(capacity),
    price: parsePrice(price),
    isSearchable: true,
    isMultiDay: !!isMultiDay,
    meta: {
      hasLiveFire: !!hasLiveFire,
      isWomenOnly: !!isWomenOnly,
    },
  };

  if (venue) {
    event.address = venue.address;
    event.coordinates = venue.coordinates;
  } else {
    event.locationId = locationId;
  }

  if (batchCreationId) {
    event.batchCreationId = batchCreationId;
  }

  if (tagline?.trim()) {
    event.tagline = tagline.trim();
  }

  if (description?.trim()) {
    event.description = description.trim();
  }

  if (topics?.length) {
    event.topics = topics;
  }

  if (hostIds?.length) {
    event.hostIds = hostIds;
  }

  if (imageUrls) {
    event.imageUrls = imageUrls;
  }

  return event;
};

export const parseScheduleEventsForms = (
  values: CabelasClassFormValues,
  userId: ?string,
  businessId: ?string,
  options: ParseScheduleCabelasClassesFormsOptions & {
    eventType?: string,
    venue?: EventVenue,
  } = {}
): Array<EventCreateRequest> => {
  const { classDates } = values;

  if (!classDates?.length) {
    throw new Error("Add at least one class date before creating classes");
  }

  const priceError = values.pricingError || getPriceError(values.price);
  if (priceError) {
    throw new Error(`Class pricing: ${priceError}`);
  }

  const batchCreationId = options.batchCreationId || createBatchCreationId();

  return classDates.map(
    (row: ScheduledClassDate, index: number): EventCreateRequest => {
      const parsedClassDate = coerceToDate(row.classDate);
      const parsedStartTime = coerceToDate(row.startTime);
      const parsedEndTime = coerceToDate(row.endTime);

      if (!parsedClassDate || !parsedStartTime || !parsedEndTime) {
        throw new Error(
          "Every class date needs a date, a start time and an end time"
        );
      }

      const capacityError = getCapacityError(row.capacity, values.maxCapacity);

      if (capacityError) {
        throw new Error(`Class ${index + 1} capacity: ${capacityError}`);
      }

      const durationError = getDurationError(
        getClassDuration(parsedStartTime, parsedEndTime),
        values
      );

      if (durationError) {
        throw new Error(`Class ${index + 1}: ${durationError}`);
      }

      return buildCabelasClassEvent(
        { ...values, capacity: row.capacity },
        userId,
        {
          startDate: combineDateAndTime(parsedClassDate, parsedStartTime),
          endDate: combineDateAndTime(parsedClassDate, parsedEndTime),
          closeDate: combineDateAndTime(parsedClassDate, DEFAULT_CLOSE_TIME),
        },
        businessId,
        batchCreationId,
        options.eventType || "class",
        options.venue
      );
    }
  );
};

// "class" ensures the events API preserves meta fields.
export const parseScheduleCabelasClassesForms = (
  values: CabelasClassFormValues,
  userId: ?string,
  businessId: ?string,
  options: ParseScheduleCabelasClassesFormsOptions = {}
): Array<EventCreateRequest> =>
  parseScheduleEventsForms(values, userId, businessId, {
    ...options,
    eventType: "class",
  });
